import { useState, type FormEvent } from 'react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Text } from '@/components/ui/Text'
import { Combobox } from '@/components/ui/Combobox'
import { cn } from '@/lib/cn'
import type { RegisterPolicy } from '@/lib/auth/register-api'
import type { Step2Data, OrgType } from './signup-types'

interface SignupStep2Props {
  policy: RegisterPolicy
  onNext: (data: Step2Data) => void
  /** Called with a snapshot of the current field values so the parent can restore them on re-entry. */
  onBack: (draft: Partial<Step2Data>) => void
  /** Restore previously entered values when the user navigates back to this step. */
  initialValues?: Partial<Step2Data>
  /** True while the registration request is in flight — disables submit and shows a loading label. */
  isSubmitting?: boolean
}

interface Step2Errors {
  companyName?: string
  countryCode?: string
}

const ORG_TYPE_ROLES = [
  { value: 'legal_counsel', label: 'Legal Counsel' },
  { value: 'company_secretary', label: 'Company Secretary' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'fund_administrator', label: 'Fund Administrator' },
  { value: 'cfo_finance', label: 'CFO / Finance' },
  { value: 'other', label: 'Other' },
]

interface ForkCardProps {
  testId: string
  selected: boolean
  onSelect: () => void
  title: string
  description: string
}

function ForkCard({ testId, selected, onSelect, title, description }: ForkCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-testid={testId}
      onClick={onSelect}
      className={cn(
        'w-full rounded-[10px] border px-3 py-3 text-left transition-all',
        selected
          ? 'border-primary bg-primary/5 shadow-[0_0_0_2px_oklch(from_var(--color-primary)_l_c_h_/_0.18)]'
          : 'border-border bg-surface hover:border-primary/40 hover:bg-primary/3',
      )}
    >
      <Text variant="label-sm" color={selected ? 'primary' : 'default'} className="mb-0.5">
        {title}
      </Text>
      <Text variant="body-sm" color="muted">
        {description}
      </Text>
    </button>
  )
}

export function SignupStep2({ policy, onNext, onBack, initialValues, isSubmitting }: SignupStep2Props) {
  const [companyName, setCompanyName] = useState(initialValues?.companyName ?? '')
  const [website, setWebsite] = useState(initialValues?.website ?? '')
  const [countryCode, setCountryCode] = useState(initialValues?.countryCode ?? '')
  const [role, setRole] = useState(initialValues?.role ?? '')
  const [orgType, setOrgType] = useState<OrgType>(initialValues?.orgType ?? 'client')
  const [errors, setErrors] = useState<Step2Errors>({})
  const [submitted, setSubmitted] = useState(false)

  const countryOptions = policy.supportedCountries.map((c) => ({
    value: c.code,
    label: `${c.flag_emoji ?? ''} ${c.name}`.trim(),
  }))

  function validate(overrides?: { companyName?: string; countryCode?: string }): Step2Errors {
    const name = overrides?.companyName ?? companyName
    const cc = overrides?.countryCode ?? countryCode
    const e: Step2Errors = {}
    if (!name.trim()) e.companyName = 'Organisation name is required'
    if (!cc) e.countryCode = 'Country is required'
    return e
  }

  function revalidate(overrides: Parameters<typeof validate>[0]) {
    if (submitted) setErrors(validate(overrides))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitted(true)
    const errs = validate()
    setErrors(errs)
    if (Object.keys(errs).length > 0) return
    onNext({ companyName, website, countryCode, role, orgType })
  }

  function fieldError(key: keyof Step2Errors) {
    return submitted ? errors[key] : undefined
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="flex flex-col gap-4">
        {/* Company name */}
        <div>
          <label htmlFor="s2-companyName">
            <Text variant="label-sm" className="mb-1 block">
              Organisation name
            </Text>
          </label>
          <Input
            id="s2-companyName"
            data-testid="step2-company-name"
            value={companyName}
            onChange={(e) => { const v = e.target.value; setCompanyName(v); revalidate({ companyName: v }) }}
            placeholder="Acme Legal Ltd."
            autoComplete="organization"
            aria-describedby={fieldError('companyName') ? 's2-company-err' : undefined}
            aria-invalid={!!fieldError('companyName')}
          />
          {fieldError('companyName') && (
            <Text id="s2-company-err" variant="body-sm" color="danger" className="mt-1">
              {errors.companyName}
            </Text>
          )}
        </div>

        {/* Website (optional) */}
        <div>
          <label htmlFor="s2-website">
            <Text variant="label-sm" className="mb-1 block">
              Website{' '}
              <Text as="span" variant="label-sm" color="muted">
                (optional)
              </Text>
            </Text>
          </label>
          <Input
            id="s2-website"
            data-testid="step2-website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://company.com"
            autoComplete="url"
            type="url"
          />
        </div>

        {/* Country */}
        <div>
          <label htmlFor="s2-country">
            <Text variant="label-sm" className="mb-1 block">
              Country of registration
            </Text>
          </label>
          <Combobox
            id="s2-country"
            data-testid="step2-country"
            value={countryCode}
            onValueChange={(v) => { setCountryCode(v); revalidate({ countryCode: v }) }}
            options={countryOptions}
            placeholder="Select country"
            aria-describedby={fieldError('countryCode') ? 's2-country-err' : undefined}
            aria-invalid={!!fieldError('countryCode')}
          />
          {fieldError('countryCode') && (
            <Text id="s2-country-err" variant="body-sm" color="danger" className="mt-1">
              {errors.countryCode}
            </Text>
          )}
        </div>

        {/* Role (optional) */}
        <div>
          <label htmlFor="s2-role">
            <Text variant="label-sm" className="mb-1 block">
              Your role{' '}
              <Text as="span" variant="label-sm" color="muted">
                (optional)
              </Text>
            </Text>
          </label>
          <Combobox
            id="s2-role"
            data-testid="step2-role"
            value={role}
            onValueChange={setRole}
            options={ORG_TYPE_ROLES}
            placeholder="Select your role"
          />
        </div>

        {/* Fork — how do you manage entities? */}
        <div>
          <Text variant="label-sm" className="mb-2 block">
            How does your organisation manage legal entities?
          </Text>
          <div role="radiogroup" aria-label="Organisation type" className="flex flex-col gap-2">
            <ForkCard
              testId="fork-card-client"
              selected={orgType === 'client'}
              onSelect={() => setOrgType('client')}
              title="We manage our own entities"
              description="You're an in-house legal, compliance, or secretarial team managing your organisation's own corporate group."
            />
            <ForkCard
              testId="fork-card-agency"
              selected={orgType === 'agency'}
              onSelect={() => setOrgType('agency')}
              title="We manage entities for other organisations"
              description="You're a law firm, fund administrator, or corporate-services provider that manages multiple client portfolios."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-2">
          <Button
            type="button"
            data-testid="step2-back-btn"
            onClick={() => onBack({ companyName, website, countryCode, role, orgType })}
            className="flex-1"
            variant="outline"
          >
            Back
          </Button>
          <Button type="submit" data-testid="step2-next-btn" className="flex-1" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Continue'}
          </Button>
        </div>
      </div>
    </form>
  )
}
